import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Textarea,
  VStack,
  Text,
  useToast,
  Badge,
  Box,
} from '@chakra-ui/react';

/**
 * ScriptEditModal Component
 * Modal for editing conversation scripts linked from chat messages
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close callback
 * @param {string} props.nodeId - Node ID being edited
 * @param {string} props.content - Current script content
 * @param {Object} props.metadata - Additional node metadata
 * @param {Function} props.onSave - Save callback
 */
const ScriptEditModal = ({
  isOpen,
  onClose,
  nodeId,
  content,
  metadata,
  onSave,
}) => {
  const [editedContent, setEditedContent] = useState(content || '');
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    setEditedContent(content || '');
  }, [content]);

  const handleSave = async () => {
    if (!editedContent.trim()) {
      toast({
        title: 'Content cannot be empty',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSaving(true);
    try {
      await onSave(nodeId, editedContent, metadata);
      toast({
        title: 'Saved successfully',
        description: 'Script has been updated',
        status: 'success',
        duration: 3000,
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Save failed',
        description: error.message || 'Please try again later',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedContent(content || '');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      isCentered
      closeOnOverlayClick={!isSaving}
    >
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <ModalHeader>
          <VStack align="start" spacing={2}>
            <Text>Edit Script</Text>
            <Badge colorScheme="purple" fontSize="sm">
              Node: {nodeId?.slice(0, 12)}...
            </Badge>
          </VStack>
        </ModalHeader>
        <ModalCloseButton isDisabled={isSaving} />

        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {/* Instructions */}
            <Box
              bg="blue.50"
              p={3}
              borderRadius="md"
              border="1px solid"
              borderColor="blue.200"
            >
              <Text fontSize="sm" color="blue.800">
                💡 Note: Changes here will directly update the conversation node's script template
              </Text>
            </Box>

            {/* Original Content */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2} color="text.secondary">
                Original Content:
              </Text>
              <Box
                bg="gray.50"
                p={3}
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
              >
                <Text fontSize="sm" color="text.muted" whiteSpace="pre-wrap">
                  {content}
                </Text>
              </Box>
            </Box>

            {/* Edit Area */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2} color="text.primary">
                Modified Content:
              </Text>
              <Textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                placeholder="Enter new script content..."
                rows={6}
                resize="vertical"
                fontSize="sm"
                isDisabled={isSaving}
                focusBorderColor="brand.blue.500"
              />
            </Box>

            {/* Metadata Info */}
            {metadata && (
              <Box fontSize="xs" color="text.muted">
                <Text>Node Type: {metadata.node_type || 'Unknown'}</Text>
                {metadata.question && <Text>Question Template: {metadata.question}</Text>}
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={handleCancel}
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Saving..."
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ScriptEditModal;
